import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { FieldError } from '@/components/feedback/FieldError'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { groupInfoSchema, groupInfoValues, type GroupInfoValues } from '@/features/admin/group-form'
import { fieldErrorProps } from '@/lib/form/field-error'
import { useRecordEditor } from '@/lib/form/use-record-editor'
import { useRecordVersion } from '@/lib/form/use-record-version'
import { messages } from '@/lib/messages'
import { ApiError, toProblem } from '@/lib/problem'
import { queryKeys } from '@/lib/query-keys'
import { services } from '@/services'
import type { Group } from '@/types/models'

const FORM_ID = 'edit-group-form'

/** Grup adı ve açıklaması. `PATCH /groups/{id}` sürüm kapısından geçer (409 `record_changed`). */
export function EditGroupDialog({ group, onClose }: { group: Group | null; onClose: () => void }) {
  const queryClient = useQueryClient()
  // `group` listeden tıklama anındaki anlık görüntüdür; form ondan kurulur. `base` o görüntü ya da
  // "Yeniden yükle"nin getirdiği güncel grup: sürüm bu kayıttan okunur, arka plan tazelemesi onu ilerletmez.
  const [base, setBase] = useState(group)
  const form = useForm<GroupInfoValues>({
    resolver: zodResolver(groupInfoSchema),
    defaultValues: groupInfoValues(group),
  })
  const version = useRecordVersion({
    recordId: group?.id ?? null,
    loaded: base?.version ?? null,
    dirty: true,
    reload: async () => {
      const list = await queryClient.fetchQuery({
        queryKey: queryKeys.admin.groups(),
        queryFn: () => services.admin.listGroups(),
        staleTime: 0,
      })
      const fresh = list.find((g) => g.id === group?.id)
      if (!fresh) return onClose()
      setBase(fresh)
      form.reset(groupInfoValues(fresh))
    },
  })

  const editor = useRecordEditor({
    form,
    submit: (values) =>
      services.admin.updateGroup(group!.id, {
        name: values.name,
        description: values.description || null,
        version: version.current(),
      }),
    onSaved: (saved) => {
      version.advance(saved.version)
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.groups() })
      onClose()
    },
    version,
    // Aynı adlı grup 409 `group_name_taken` döner; mesaj adın altında görünür.
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'group_name_taken') {
        form.setError(
          'name',
          { type: 'server', message: toProblem(error).detail ?? error.message },
          { shouldFocus: true },
        )
        return true
      }
      return false
    },
  })

  const errors = form.formState.errors

  return (
    <Dialog open={group !== null} onOpenChange={(open) => !open && editor.requestClose(onClose)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{messages.groups.editTitle}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {version.alert}
          {version.dialog}
          {editor.dialog}
          {group && (
            <form
              id={FORM_ID}
              className="flex flex-col gap-3"
              noValidate
              onSubmit={(event) => void editor.save(event)}
            >
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-group-name">{messages.groups.name}</Label>
                <Input
                  id="edit-group-name"
                  {...fieldErrorProps('edit-group-name', errors.name?.message)}
                  {...form.register('name')}
                />
                <FieldError id="edit-group-name" message={errors.name?.message} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-group-description">{messages.groups.descriptionField}</Label>
                <Input
                  id="edit-group-description"
                  {...fieldErrorProps('edit-group-description', errors.description?.message)}
                  {...form.register('description')}
                />
                <FieldError id="edit-group-description" message={errors.description?.message} />
              </div>
            </form>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => editor.requestClose(onClose)}>
            {messages.common.cancel}
          </Button>
          <Button type="submit" form={FORM_ID} disabled={editor.saving}>
            {messages.common.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
